import {
	RoleType,
	SituationType,
	Type,
	SumType,
	ProductType,
} from "../annotation/ontology";
import {
	SituationAnnotationScheme,
	SituationMentionAnnotationScheme,
} from "../annotation/scheme";
import { generateExpressionTree } from "./expressionTree";
import { Node } from "./expressionTree";
import { Situation as SituationIntermediate } from "../structure/intermediates";

export function initializeRoleSetMap(
	situationOntologyType: string | undefined,
	situationMentionAnnotationScheme:
		| SituationMentionAnnotationScheme
		| SituationAnnotationScheme
): Map<string, (RoleType | SumType | ProductType)[]> {
	const map = new Map<string, (RoleType | SumType | ProductType)[]>();
	if (situationOntologyType === "particular") {
		situationMentionAnnotationScheme?.ontology.types.forEach((t, index) => {
			t = t as SituationType;
			map.set(t.value, t.roleSet?.types || []);
		});
	} else if (situationOntologyType === "general") {
		map.set(
			"types",
			situationMentionAnnotationScheme?.ontology.roleOntology?.types || []
		);
	}

	return map;
}

export function initializeSituationExpressionTree(
	situationOntologyType: string | undefined,
	situationMentionAnnotationScheme:
		| SituationMentionAnnotationScheme
		| SituationAnnotationScheme
): Map<string, Node> {
	const map = new Map<string, Node>();

	// Placeholder for testing expressionTrees
	const defaultOperation = "product";

	//Fill in details here for creating the trees for every situation; general and particular supported
	if (situationOntologyType === "particular") {
		situationMentionAnnotationScheme?.ontology.types.forEach(
			(type, idx) => {
				const expr = [];
				type = type as SituationType;
				type.roleSet?.types.forEach((role, idx) => {
					expr.push(role.value);
				});

				const len = expr.length;

				for (var i = 0; i < len - 1; i++) {
					expr.push(defaultOperation);
				}

				const exprTree = generateExpressionTree(expr);

				if (exprTree) map.set(type.value, exprTree);
			}
		);
	} else if (situationOntologyType === "general") {
		const expr = [];
		situationMentionAnnotationScheme?.ontology.roleOntology?.types.forEach(
			(type, idx) => {
				expr.push(type.value);
			}
		);

		const len = expr.length;

		for (var i = 0; i < len - 1; i++) {
			expr.push(defaultOperation);
		}

		const exprTree = generateExpressionTree(expr);

		if (exprTree) map.set("situation", exprTree);
	}

	return map;
}

/**
 * Initializes a HashMap that stores
 * situationName-situationArgument as key and
 * the entity/situation/text as value
 *
 * TODO: Add support for situation/text/options as value
 *
 * @returns HashMap filled with entity set loaded from concrete file
 * filtered by metadata
 */
export function initializeSituationArgumentMap(
	situationList: SituationIntermediate[]
): Map<string, Map<string, Set<string>>> {
	const argumentMap = new Map<string, Map<string, Set<string>>>();

	situationList.forEach((situation, index) => {
		if (!argumentMap.has(situation.uuid.uuidString))
			argumentMap.set(
				situation.uuid.uuidString,
				new Map<string, Set<string>>()
			);
		const situationAddress = argumentMap.get(situation.uuid.uuidString);
		situation.argumentList?.forEach((arg, idx) => {
			if (arg.role) {
				if (arg.entityId) {
					situationAddress?.set(arg.role, new Set(arg.entityId));
				} else if (arg.situationId) {
					situationAddress?.set(arg.role, new Set(arg.situationId));
				}
			}
		});
	});

	return argumentMap;
}

export function initializeArgumentOntologyMap(
	annotationScheme: SituationMentionAnnotationScheme
): Map<string, Type | SumType | ProductType> {
	const map = new Map<string, Type | SumType | ProductType>();

	annotationScheme.ontology.argumentOntology?.types.forEach((element) => {
		map.set(element.uuid.uuidString, element);
	});

	return map;
}

export default {
	initializeRoleSetMap,
	initializeSituationExpressionTree,
	initializeSituationArgumentMap,
	initializeArgumentOntology: initializeArgumentOntologyMap,
};
